import { capitalize } from '@iotv/datamodel-core';
import { createContext } from 'react';
import { colorMap } from '../cosmetics/ranges';
import { deOverlapRanges } from '../fns/deoverlapRanges';
import { getFutureSchedules, getPeriodStates } from '../fns/getPeriodStates';
import { isIoTVCommonRollingProgrammeEnabledDefDownlink, isIoTVCommonRollingProgrammeEnabledDefUplink, isIoTVCommonSequentialProgrammeEnabledDefDownlink, isIoTVCommonSequentialProgrammeEnabledDefUplink, } from '@iotv/lfc-devices';
/* ref https://developers.google.com/chart/interactive/docs/gallery/timeline */
const debug = process.env.REACT_APP_DEBUG && false;
export const defaultReducerState = {
    entity: null,
    device: null,
    deviceConfig: null,
    broadcastSchedule: null,
    fromDateValue: null,
    periodStates: null,
    recentStateHistory: null,
    slicedStore: null,
    sortedReportedSchedules: null,
    toDateValue: null,
    rows: null,
    useReportedRequested: 'REPORTED',
};
export const operationalStatusReducer = (prev, action) => {
    const update = { ...prev };
    const { entity: entityIn, fromDateValue: fromDateValueIn, toDateValue: toDateValueIn, recentStateHistory: recentStateHistoryIn = null, device: deviceIn = null, deviceConfig: deviceConfigIn = null, broadcastSchedule: broadcastScheduleIn = null, useReportedRequested: useReportedRequestedIn, } = action;
    if (entityIn) {
        update.entity = entityIn;
    }
    if (useReportedRequestedIn) {
        update.useReportedRequested = useReportedRequestedIn;
        Object.assign(update, recalculate(update));
    }
    if (broadcastScheduleIn) {
        update.broadcastSchedule = broadcastScheduleIn;
    }
    if (deviceConfigIn) {
        update.deviceConfig = deviceConfigIn;
        Object.assign(update, recalculate(update));
    }
    if (deviceIn) {
        update.device = deviceIn;
    }
    if (recentStateHistoryIn) {
        update.recentStateHistory = recentStateHistoryIn;
    }
    // if (currentSequentialSchedulesIn) {
    //     update.currentSequentialSchedules = currentSequentialSchedulesIn
    // }
    if (fromDateValueIn && toDateValueIn) {
        update.fromDateValue = fromDateValueIn;
        update.toDateValue = toDateValueIn;
    }
    if (update.recentStateHistory) {
        const { slicedStore, periodStates } = getPeriodStates({
            recentStateHistory: update.recentStateHistory,
            fromDateValue: update.fromDateValue,
            toDateValue: update.toDateValue,
        });
        update.periodStates = periodStates;
        update.slicedStore = slicedStore;
    }
    //>> CHNAGE THIS TO BE FROM DEVICE CONFIG, ADD ROLLING, REMOVE ACTION THAT SUBMITS currentSequentialSchedules
    // if (update.currentSequentialSchedules && update.toDateValue && update.fromDateValue) {
    //     const { sortedReportedSchedules, } = getFutureSchedules({ sequentialSchedules: update.currentSequentialSchedules, fromDateValue: update.fromDateValue, toDateValue: update.toDateValue })
    //     update.sortedReportedSchedules = sortedReportedSchedules
    // }
    const ranges = [...(update.periodStates ?? []), ...(update.sortedReportedSchedules ?? [])];
    const deOverlappedRanges = deOverlapRanges({ ranges });
    const rows = deOverlappedRanges.map((dr) => getRowFromDateRange({ entity: update.entity, dr }));
    if (rows.length > 0) {
        debug && console.log('adding rows', rows);
        update.rows = rows;
    }
    else if (update.fromDateValue && update.toDateValue) {
        debug && console.log('no rows', rows);
        const noDataRows = [[`${update.entity?.name ?? ''}`, 'No data', 'red', update.fromDateValue?.toDate(), update.toDateValue?.toDate()]];
        update.rows = noDataRows;
    }
    else {
        update.rows = null;
    }
    debug && console.log('opererationState reducer got', { prev, action, update });
    return update;
};
export const OperationalStateReducerContext = createContext([
    defaultReducerState,
    (next) => {
        throw new Error('Reducer not initialized');
    },
]);
const recalculate = (current) => {
    const update = { ...current };
    if (update.deviceConfig) {
        const { requested, reported } = update.deviceConfig;
        const { fromDateValue, toDateValue } = current;
        if (update.useReportedRequested === 'REQUESTED' && fromDateValue && toDateValue) {
            if (isIoTVCommonSequentialProgrammeEnabledDefDownlink(requested)) {
                const { sortedReportedSchedules } = getFutureSchedules({ sequentialSchedules: requested.scheduleSet, fromDateValue, toDateValue });
                update.sortedReportedSchedules = sortedReportedSchedules;
            }
            else if (isIoTVCommonRollingProgrammeEnabledDefDownlink(requested)) {
                const rollingPatternRequestAsEnabled = { ...requested.rollingPatternMode, enabled: 1 };
                const { sortedReportedSchedules } = getFutureSchedules({ rollingPatternReport: rollingPatternRequestAsEnabled, fromDateValue, toDateValue });
                update.sortedReportedSchedules = sortedReportedSchedules;
            }
            else {
            }
        }
        else if (update.useReportedRequested === 'REPORTED' && fromDateValue && toDateValue) {
            if (isIoTVCommonSequentialProgrammeEnabledDefUplink(reported)) {
                const { sortedReportedSchedules } = getFutureSchedules({ sequentialSchedules: reported.scheduleReport.config, fromDateValue, toDateValue });
                update.sortedReportedSchedules = sortedReportedSchedules;
            }
            else if (isIoTVCommonRollingProgrammeEnabledDefUplink(reported)) {
                const rollingPatternRequestAsEnabled = { ...reported.scheduleReport.rollingPatternConfig, enabled: 1 };
                const { sortedReportedSchedules } = getFutureSchedules({ rollingPatternReport: rollingPatternRequestAsEnabled, fromDateValue, toDateValue });
                update.sortedReportedSchedules = sortedReportedSchedules;
            }
            else {
            }
        }
        else {
            console.log(`WARN: FALL THROUGH`, update);
        }
    }
    return update;
};
const getRowFromDateRange = ({ entity, dr }) => {
    const defaultColor = 'aquamarine';
    let color = defaultColor;
    const { operatingState, errorsAndWarnings } = dr;
    const primaryDisplayState = errorsAndWarnings?.length > 0 ? errorsAndWarnings[0] : operatingState?.[0];
    let selectedOperatingState = primaryDisplayState;
    if (selectedOperatingState && colorMap[selectedOperatingState]) {
        color = colorMap[selectedOperatingState];
    }
    else {
        selectedOperatingState = 'Scheduled';
    }
    return [`${entity?.name}`, capitalize(selectedOperatingState.replace(/_/g, ' ').toLowerCase()) ?? '', color, ...dr.dateRange];
};
